<script setup lang="ts">
import { ref } from "vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { ElDialog } from "element-plus";
import LegendViewerState from "@/stores/LegendViewerState";
import LegendLib from "@/utils/LegendLib";
import HatchPattern from "@/components/legends/HatchPattern.vue";
import {
  lineweightLabel,
  linetypeLabel,
} from "@/components/legends/configurator/utils";
import HatchLibrary from "@/hatches/library";
import type { NoteConfig, ScaleConfig } from "@/types/legend.types";
import { useTranslation } from "i18next-vue";

const { t } = useTranslation();

type Tab = "disorders" | "layers" | "scales";
const activeTab = ref<Tab>("disorders");

const layerName = (layerId?: string): string | undefined => {
  if (!layerId) return undefined;
  return LegendViewerState.legend?.layer_configs.find((l) => l.id === layerId)
    ?.name;
};

// Même repli que NoteMenuState.computeNoteConfig : tant que l'utilisateur n'a pas
// saisi de userScale, l'échelle effective retombe sur la baseScale de la bibliothèque.
const hatchScale = (config: NoteConfig): number | undefined => {
  if (!config.hatch) return undefined;
  return (
    config.hatch.userScale ??
    HatchLibrary.getByName(config.hatch.name)?.baseScale
  );
};

// Même repli que NoteMenuState.computeNoteConfig/ScaleMenuState pour la hauteur de
// texte effective : textsize (racine) prime sur textstyle.size.
const textHeight = (config: ScaleConfig): number | undefined =>
  config.props?.textsize ?? config.props?.textstyle?.size;

const fontName = (config: ScaleConfig): string | undefined =>
  config.props?.textstyle?.typeface ?? config.props?.textstyle?.filename;
</script>

<template>
  <ElDialog
    v-model="LegendViewerState.isOpen"
    :title="
      $t('canvas:legend.viewer') +
      ' : ' +
      (LegendViewerState.legend?.name ?? '')
    "
    :append-to-body="true"
    class="legend-viewer-dialog"
    width="min(680px, 95vw)"
    top="4vh"
  >
    <template v-if="LegendViewerState.legend">
      <!-- Tab bar -->
      <div class="lv-tabs">
        <button
          class="lv-tab"
          :class="{ active: activeTab === 'disorders' }"
          @click="activeTab = 'disorders'"
        >
          <PhosphorIcon name="wall" :size="16" />
          {{ $t("defects") }}
          <span class="lv-count">{{
            LegendViewerState.legend.note_configs.length
          }}</span>
        </button>
        <button
          class="lv-tab"
          :class="{ active: activeTab === 'layers' }"
          @click="activeTab = 'layers'"
        >
          <PhosphorIcon name="stack" :size="16" />
          {{ $t("layers") }}
          <span class="lv-count">{{
            LegendViewerState.legend.layer_configs.length
          }}</span>
        </button>
        <button
          class="lv-tab"
          :class="{ active: activeTab === 'scales' }"
          @click="activeTab = 'scales'"
        >
          <PhosphorIcon name="text-t" :size="16" />
          {{ $t("textstyles") }}
          <span class="lv-count">{{
            LegendViewerState.legend.scale_configs.length
          }}</span>
        </button>
      </div>

      <!-- Tab: Disorders -->
      <div v-if="activeTab === 'disorders'" class="lv-body">
        <div class="disorder-grid">
          <div
            v-for="config in LegendViewerState.legend.note_configs"
            :key="config.name"
            class="disorder-card"
          >
            <div class="disorder-card-left">
              <HatchPattern
                v-if="config.hatch"
                :hatch-name="config.hatch.name"
                :rotation="config.hatch.rotation"
                :size="36"
              />
              <div v-else class="disorder-hatch-placeholder" />
              <span
                class="disorder-swatch"
                :style="{
                  backgroundColor: LegendLib.getComputedColor(
                    config,
                    LegendViewerState.legend!,
                  ),
                }"
              />
            </div>
            <div class="disorder-card-info">
              <span class="disorder-name">{{ config.name }}</span>
              <span v-if="config.label" class="disorder-label"
                >({{ config.label }})</span
              >
              <div class="disorder-meta">
                <span v-if="layerName(config.props.layerId)" class="meta-badge">
                  <PhosphorIcon name="stack" :size="11" />
                  {{ layerName(config.props.layerId) }}
                </span>
                <span
                  v-if="config.props.lineweight !== undefined"
                  class="meta-badge"
                  >{{ lineweightLabel(config.props.lineweight, t) }}</span
                >
                <span v-if="config.props.linetype" class="meta-badge">{{
                  linetypeLabel(config.props.linetype, t)
                }}</span>
                <span v-if="hatchScale(config) !== undefined" class="meta-badge"
                  >Éch. {{ hatchScale(config) }}</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab: Layers -->
      <div v-else-if="activeTab === 'layers'" class="lv-body">
        <div class="layer-grid">
          <div
            v-for="config in LegendViewerState.legend.layer_configs"
            :key="config.name"
            class="layer-row"
          >
            <span
              class="layer-swatch"
              :style="{
                backgroundColor: LegendLib.getLayerColor(
                  config.id,
                  LegendViewerState.legend!,
                ),
              }"
            />
            <div class="layer-info">
              <span class="layer-name">{{ config.name }}</span>
              <div class="disorder-meta">
                <span
                  v-if="config.lineweight !== undefined"
                  class="meta-badge"
                  >{{ lineweightLabel(config.lineweight, t) }}</span
                >
                <span v-if="config.linetype" class="meta-badge">{{
                  config.linetype
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab: Text styles -->
      <div v-else-if="activeTab === 'scales'" class="lv-body">
        <div class="scale-list">
          <div
            v-for="config in LegendViewerState.legend.scale_configs"
            :key="config.name"
            class="scale-row"
          >
            <span class="scale-name">{{ config.name }}</span>
            <div class="disorder-meta">
              <span v-if="textHeight(config) !== undefined" class="meta-badge"
                >H {{ textHeight(config) }}</span
              >
              <span v-if="fontName(config)" class="meta-badge">{{
                fontName(config)
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <button
        class="canvas-btn canvas-btn-sm"
        @click="LegendViewerState.hide()"
      >
        {{ $t("close") }}
      </button>
    </template>
  </ElDialog>
</template>

<!-- Non-scoped: reaches the teleported dialog element -->
<style lang="scss">
.el-dialog.legend-viewer-dialog {
  background-color: var(--canvas-bg);
  border: 1px solid var(--canvas-border);
  border-radius: 12px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6);

  .el-dialog__header {
    background-color: var(--canvas-bg);
    border-bottom: 1px solid var(--canvas-border);
    padding: 14px 20px;
    margin-right: 0;
  }

  .el-dialog__title {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    font-weight: 600;
  }

  .el-dialog__body {
    padding: 0;
    background-color: var(--canvas-bg);
    color: rgba(255, 255, 255, 0.85);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .el-dialog__footer {
    background-color: var(--canvas-bg);
    border-top: 1px solid var(--canvas-border);
    padding: 10px 16px;
    display: flex;
    justify-content: flex-end;
  }

  .el-dialog__headerbtn .el-icon {
    color: rgba(255, 255, 255, 0.5);

    &:hover {
      color: rgba(255, 255, 255, 0.9);
    }
  }
}
</style>

<style lang="scss" scoped>
.lv-tabs {
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid var(--canvas-border);
  background-color: rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.lv-tab {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 10px 16px;
  font-size: 0.78rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  background: transparent;
  border: none;
  border-right: 1px solid var(--canvas-border);
  cursor: pointer;
  white-space: nowrap;
  transition:
    color 0.15s,
    background 0.15s;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
    background: rgba(255, 255, 255, 0.05);
  }

  &.active {
    color: #fff;
    background: var(--canvas-bg);
    box-shadow: inset 0 2px 0 0 var(--bs-primary);
  }
}

.lv-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 16px;
  padding: 0 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  font-size: 0.65rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);

  .lv-tab.active & {
    background: rgba(var(--bs-primary-rgb), 0.2);
    color: rgba(255, 255, 255, 0.8);
  }
}

.lv-body {
  overflow-y: auto;
  padding: 12px;
  max-height: calc(85dvh - 170px);
}

// ── Disorders ─────────────────────────────────────────────

.disorder-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;

  @media (min-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 680px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
}

.disorder-card {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--canvas-border);
  border-radius: 8px;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
  }
}

.disorder-card-left {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.disorder-hatch-placeholder {
  width: 36px;
  height: 36px;
}

.disorder-swatch {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: inline-block;
  flex-shrink: 0;
}

.disorder-card-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.disorder-name {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.disorder-label {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.45);
}

.disorder-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 3px;
}

.meta-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
}

// ── Layers ────────────────────────────────────────────────

.layer-grid {
  display: grid;
  grid-template-columns: 1fr;

  @media (min-width: 400px) {
    grid-template-columns: 1fr 1fr;
  }
}

.layer-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--canvas-border);

  &:last-child {
    border-bottom: none;
  }
}

.layer-swatch {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
}

.layer-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.layer-name {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.85);
}

// ── Text Styles ───────────────────────────────────────────

.scale-list {
  display: flex;
  flex-direction: column;
}

.scale-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--canvas-border);

  &:last-child {
    border-bottom: none;
  }
}

.scale-name {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.85);
}
</style>
